import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import {
  IFSecureOrchestrator,
  IFsecureReactivationOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import {
  IFSecure,
  IServiceStatus,
  useProducts,
} from "@nju/data-access-portal-product";
import { GeneralErrorNotification, Panel, Separator, Text } from "@nju/ui";
import { Buttons } from "./Buttons";
import { fSecureServiceCTABannerMachine } from "./fSecureServiceCTABanner.machine";
import { getFSecureServiceCTABannerConditions } from "./getFSecureServiceCTABannerConditions";
import { getFSecureStatusData } from "./getFSecureStatusData";

interface IServiceCTABannerProps {
  anniversaryDate: string | undefined;
  orchestrator: IFSecureOrchestrator | undefined;
  serviceStatus: IServiceStatus["fSecureServiceStatus"];
  reactivationOrchestratorStatus:
    | IFsecureReactivationOrchestrator["state"]
    | undefined;
  access: IFSecure | undefined;
}

export function FSecureServiceCTABanner({
  anniversaryDate,
  serviceStatus,
  orchestrator,
  reactivationOrchestratorStatus,
  access,
}: IServiceCTABannerProps) {
  const { reload } = useRouter();
  const { fSecureProducts } = useProducts();

  const [current, send] = useMachine(fSecureServiceCTABannerMachine, {
    context: {
      productId: fSecureProducts?.fSecure?.id,
      currentStatus: serviceStatus,
    },
    actions: {
      onSubscribeSuccess: () => {
        reload();
      },
      onRetryPaymentSuccess: () => {
        reload();
      },
    },
  });

  const isLoading = current.matches("subscribe");

  const subscribe = React.useCallback(
    (status: IServiceStatus["fSecureServiceStatus"] | "AlmostTerminated") => {
      send({ type: "SUBSCRIBE", currentStatus: status });
    },
    [send],
  );

  const refresh = React.useCallback(() => {
    reload();
  }, [reload]);

  const conditions = React.useMemo(() => {
    return getFSecureServiceCTABannerConditions({
      orchestrator,
      serviceStatus,
      reactivationOrchestratorStatus,
    });
  }, [orchestrator, serviceStatus, reactivationOrchestratorStatus]);

  const panelProps = React.useMemo(() => {
    return getFSecureStatusData({
      anniversaryDate,
      conditions,
      access,
    });
  }, [conditions, anniversaryDate, access]);

  if (!panelProps) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const { text, title, iconName } = panelProps;

  if (current.matches("error")) {
    return (
      <>
        <GeneralErrorNotification
          callback={() => {
            reload();
          }}
        />
        <Separator size="xs" tone="invisible" />
      </>
    );
  }

  return (
    <>
      <Panel
        iconName={iconName}
        isLoading={isLoading}
        testId="ftth-service-status-banner"
        title={title}
      >
        <Separator size="xs" tone="invisible" />
        <Text
          size={{
            "@onlyMobile": "m",
            "@fromTablet": "l",
          }}
        >
          {text}
        </Text>
        <Separator size="xs" tone="invisible" />
        <Buttons
          access={access}
          conditions={conditions}
          fSecureServiceStatus={serviceStatus}
          refreshCallback={refresh}
          subscribeCallback={subscribe}
        />
      </Panel>
      <Separator size="xs" tone="invisible" />
    </>
  );
}
